import React from 'react';
import { arrayOf, oneOfType, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { propTypes } from '../../util/types';

import NoImageIcon from './NoImageIcon';
import css from './ResponsiveImage.module.css';

const ResponsiveImage = props => {
  const {
    className,
    rootClassName,
    alt,
    noImageMessage,
    image,
    variants,
    sizes,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  // No image → fallback icon
  if (!image || !variants || variants.length === 0) {
    const noImageClasses = classNames(
      rootClassName || css.root,
      css.noImageContainer,
      className
    );
    const noImageMessageText =
      noImageMessage || <FormattedMessage id="ResponsiveImage.noImage" />;

    return (
      <div className={noImageClasses}>
        <div className={css.noImageWrapper}>
          <NoImageIcon className={css.noImageIcon} />
          <div className={css.noImageText}>{noImageMessageText}</div>
        </div>
      </div>
    );
  }

  const imageVariants = image.attributes?.variants || {};

  // Build srcSet safely
  const srcSetItems = variants
    .map(variantName => {
      const variant = imageVariants[variantName];
      return variant ? `${variant.url} ${variant.width}w` : null;
    })
    .filter(Boolean);

  const srcSet = srcSetItems.join(', ');

  // SSR-SAFE FALLBACK: use the first valid variant as `src`
  const firstVariantKey = variants.find(v => imageVariants[v]);
  const fallbackSrc = firstVariantKey
    ? imageVariants[firstVariantKey].url
    : null;

  const imgProps = {
    className: classes,
    src: fallbackSrc,     // <— THIS FIXES SSR HYDRATION
    srcSet,
    alt,
    sizes: sizes || undefined,
    ...rest,
  };

  return <img {...imgProps} />;
};

ResponsiveImage.defaultProps = {
  className: null,
  rootClassName: null,
  image: null,
  sizes: null,
  noImageMessage: null,
};

ResponsiveImage.propTypes = {
  className: string,
  rootClassName: string,
  alt: string.isRequired,
  image: oneOfType([propTypes.image, propTypes.imageAsset]),
  variants: arrayOf(string).isRequired,
  sizes: string,
  noImageMessage: string,
};

export default ResponsiveImage;
